// ════════════════════════════════════════════════════════════
// toast-shortcuts — Helpers métier ECR
// ════════════════════════════════════════════════════════════
// Wraps fréquents pour éviter les libellés répétés dans 50 endroits.
// Tous acceptent un dernier argument `options` qui peut override les
// défauts (durée, description, position, etc.). Cf. piège #8 du brief.
//
// Tonalité : française, directe, pas excessivement polie. Aligné sur
// le ton V1 que les users connaissent déjà.
// ════════════════════════════════════════════════════════════
import type { ToastOptions } from './types';

type ToastId = string | number;

interface BaseToastWithSuccess {
  success: (message: string, options?: ToastOptions) => ToastId;
}

export interface ToastShortcuts {
  /** Modifications enregistrées (génie sans nom : 2 s par défaut). */
  saved(options?: ToastOptions): ToastId;
  /** `${name} enregistré` (Voyage MSY-042-05-26, dépense DEP-042, etc.). */
  savedAs(name: string, options?: ToastOptions): ToastId;
  /**
   * `${name} supprimé`. Si `onUndo` est fourni, ajoute un bouton « Annuler »
   * (durée allongée à 6 s pour laisser le temps d'annuler).
   */
  deleted(name: string, onUndo?: () => void, options?: ToastOptions): ToastId;
}

export type ToastWithShortcuts<T extends BaseToastWithSuccess> = T & ToastShortcuts;

export function attachShortcuts<T extends BaseToastWithSuccess>(base: T): ToastWithShortcuts<T> {
  const shortcuts: ToastShortcuts = {
    saved(options) {
      return base.success('Modifications enregistrées', {
        duration: 2000,
        ...options,
      });
    },
    savedAs(name, options) {
      return base.success(`${name} enregistré`, options);
    },
    deleted(name, onUndo, options) {
      return base.success(`${name} supprimé`, {
        duration: onUndo ? 6000 : 4000,
        action: onUndo
          ? {
              label: 'Annuler',
              onClick: () => onUndo(),
            }
          : undefined,
        ...options,
      });
    },
  };
  return Object.assign(base, shortcuts);
}
